import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Input, Select, DatePicker, message, Row, Col, Statistic, Divider, Descriptions } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const ThirdPartyWithdrawals = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, page: 1, limit: 20 });
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('detail');
  const [detailData, setDetailData] = useState(null);
  const [auditForm] = Form.useForm();
  const [appList, setAppList] = useState([]);

  useEffect(() => {
    loadData();
    loadAppList();
  }, [pagination.page]);

  const loadAppList = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { status: 1, limit: 100 } });
      if (response.code === 200) {
        setAppList(response.data.list || []);
      }
    } catch (error) {
      console.error('加载应用列表失败', error);
    }
  };

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/third-party-withdrawals', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list || []);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          total_amount: response.data.pagination.total_amount,
          page: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
    loadData({ ...newFilters, page: 1 });
  };

  const handleReset = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    loadData({ page: 1 });
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setDetailData(response.data);
        setModalMode('detail');
        setModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleAudit = async (record) => {
    try {
      const response = await api.get(`/admin/third-party-withdrawals/${record.id}`);
      if (response.code === 200) {
        setDetailData(response.data);
        auditForm.setFieldsValue({ audit_status: 'approved', audit_remark: '' });
        setModalMode('audit');
        setModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleAuditSubmit = async () => {
    try {
      const values = await auditForm.validateFields();
      const response = await api.put(`/admin/third-party-withdrawals/${detailData.id}/audit`, values);
      if (response.code === 200) {
        message.success(response.message || '审核成功');
        setModalVisible(false);
        loadData();
      }
    } catch (error) {
      message.error(error.message || '审核失败');
    }
  };

  const getStatusTag = (status, auditStatus) => {
    if (auditStatus === 'approved') {
      return <Tag color="green">已通过</Tag>;
    }
    if (auditStatus === 'rejected') {
      return <Tag color="red">已拒绝</Tag>;
    }
    const statusMap = {
      pending: { color: 'orange', text: '待审核' },
      processing: { color: 'blue', text: '处理中' },
      paid: { color: 'green', text: '已完成' },
      failed: { color: 'red', text: '失败' }
    };
    return <Tag color={statusMap[status]?.color || 'default'}>{statusMap[status]?.text || status}</Tag>;
  };

  const renderWithdrawInfo = () => {
    if (!detailData?.withdraw_info) return '-';
    
    const info = typeof detailData.withdraw_info === 'string' 
      ? JSON.parse(detailData.withdraw_info) 
      : detailData.withdraw_info;

    if (!info || Object.keys(info).length === 0) return '-';

    return (
      <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
        <Row gutter={[16, 8]}>
          {Object.entries(info).map(([key, value]) => (
            <Col span={12} key={key}>
              <span style={{ color: '#666' }}>{key}: </span>
              <span style={{ fontWeight: 500 }}>{value}</span>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: '代付单号',
      dataIndex: 'withdraw_no',
      key: 'withdraw_no',
      width: 180
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 80
    },
    {
      title: '代付金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (val, record) => `${record.currency_code || 'CNY'} ${Number(val || 0).toFixed(2)}`
    },
    // {
    //   title: '人民币金额',
    //   dataIndex: 'cny_amount',
    //   key: 'cny_amount',
    //   width: 120,
    //   render: (val) => val ? `¥${Number(val).toFixed(2)}` : '-'
    // },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 100,
      render: (val) => val ? Number(val).toFixed(2) : '-'
    },
    {
      title: '代付方式',
      dataIndex: 'withdraw_method',
      key: 'withdraw_method',
      width: 100
    },
    {
      title: '代付信息',
      dataIndex: 'withdraw_info',
      key: 'withdraw_info',
      width: 250,
      render: (info) => {
        if (!info) return '-';
        if (typeof info === 'string') {
          try { info = JSON.parse(info); } catch (e) { return info; }
        }
        const keys = Object.keys(info);
        if (keys.length === 0) return '-';
        return keys.slice(0, 4).map(k => `${k}: ${info[k]}`).join(', ') ;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => getStatusTag(status, record.audit_status)
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.audit_status === 'pending' && (
            <Button type="link" size="small" onClick={() => handleAudit(record)}>
              审核
            </Button>
          )}
        </Space>
      )
    }
  ];

  const renderModalFooter = () => {
    if (modalMode === 'detail') {
      return [
        <Button key="close" onClick={() => setModalVisible(false)}>关闭</Button>
      ];
    }
    return [
      <Button key="cancel" onClick={() => setModalVisible(false)}>取消</Button>,
      <Button key="submit" type="primary" onClick={handleAuditSubmit}>提交审核</Button>
    ];
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleSearch}>
          <Form.Item name="app_id" label="应用">
            <Select placeholder="请选择应用" style={{ width: 180 }} allowClear showSearch optionFilterProp="children">
              {appList.map(app => (
                <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="processing">处理中</Option>
              <Option value="paid">已完成</Option>
              <Option value="failed">失败</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="audit_status" label="审核状态">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="代付单号" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="dateRange" label="日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Statistic title="代付总笔数" value={pagination.total} />
          </Col>
          <Col>
            <Statistic title="代付总金额" value={Number(pagination.total_amount || 0)} precision={2} suffix="" />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, limit: pageSize });
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={modalMode === 'audit' ? '审核代付' : '代付详情'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={renderModalFooter()}
        width={800}
      >
        {detailData && (
          <div>
            <Divider orientation="left">基本信息</Divider>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="代付单号">{detailData.withdraw_no}</Descriptions.Item>
              <Descriptions.Item label="应用名称">{detailData.app_name}</Descriptions.Item>
              <Descriptions.Item label="商户用户">{detailData.user_real_name} ({detailData.user_phone})</Descriptions.Item>
              <Descriptions.Item label="代付方式">{detailData.withdraw_method}</Descriptions.Item>
              <Descriptions.Item label="代付金额">
                {detailData.currency_code} {Number(detailData.amount || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="汇率">{detailData.exchange_rate || 1}</Descriptions.Item>
              <Descriptions.Item label="手续费">{Number(detailData.fee_amount || 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="实际到账">{Number(detailData.actual_amount || 0).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="回调地址">{detailData.callback_url || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(detailData.status, detailData.audit_status)}</Descriptions.Item>
              <Descriptions.Item label="审核状态">
                {detailData.audit_status === 'approved' ? '已通过' : detailData.audit_status === 'rejected' ? '已拒绝' : '待审核'}
              </Descriptions.Item>
              <Descriptions.Item label="交易号">{detailData.transaction_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {detailData.created_at ? dayjs(detailData.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="审核时间">
                {detailData.audit_time ? dayjs(detailData.audit_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="审核人">{detailData.audit_by_name || '-'}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">代付信息</Divider>
            <div>{renderWithdrawInfo()}</div>

            {detailData.audit_status !== 'pending' && (
              <>
                <Divider orientation="left">审核信息</Divider>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="审核备注">{detailData.audit_remark || '-'}</Descriptions.Item>
                </Descriptions>
              </>
            )}

            {modalMode === 'audit' && detailData.audit_status === 'pending' && (
              <div style={{ marginTop: 24 }}>
                <Divider orientation="left">审核操作</Divider>
                <Form form={auditForm} layout="vertical">
                  <Form.Item name="audit_status" label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
                    <Select placeholder="请选择审核结果">
                      <Option value="approved">通过</Option>
                      <Option value="rejected">拒绝</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="audit_remark" label="审核备注">
                    <TextArea rows={3} placeholder="请输入审核备注（可选）" />
                  </Form.Item>
                </Form>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ThirdPartyWithdrawals;
